import { Controller } from "@hotwired/stimulus";

// Language code to country code mapping for flags
const LANGUAGE_TO_COUNTRY = {
  en: "us",
  es: "es",
  fr: "fr",
  it: "it",
  de: "de",
  pt: "pt",
  ru: "ru",
  ja: "jp",
  ko: "kr",
  ar: "sa",
  hi: "in",
  th: "th",
  vi: "vn",
  id: "id",
  ms: "my",
  tl: "ph",
  nl: "nl",
  pl: "pl",
  tr: "tr",
  el: "gr",
  iw: "il",
  sv: "se",
  no: "no",
  da: "dk",
  fi: "fi",
  cs: "cz",
  hu: "hu",
  ro: "ro",
  uk: "ua",
  bn: "bd",
  ta: "in",
  te: "in",
  mr: "in",
  gu: "in",
  kn: "in",
  ml: "in",
  pa: "in",
  ur: "pk",
  fa: "ir",
  ne: "np",
  si: "lk",
  my: "mm",
  km: "kh",
  lo: "la",
  mn: "mn",
  kk: "kz",
  uz: "uz",
  az: "az",
  ka: "ge",
  hy: "am",
  sq: "al",
  sr: "rs",
  hr: "hr",
  sl: "si",
  sk: "sk",
  bg: "bg",
  mk: "mk",
  bs: "ba",
  is: "is",
  et: "ee",
  lv: "lv",
  lt: "lt",
  mt: "mt",
  ga: "ie",
  cy: "gb",
  ca: "es",
  eu: "es",
  gl: "es",
  af: "za",
  sw: "ke",
  zu: "za",
  xh: "za",
  am: "et",
  so: "so",
  ha: "ng",
  yo: "ng",
  ig: "ng",
  "zh-CN": "cn",
  "zh-TW": "tw",
};

// Language names in English
const LANGUAGE_NAMES = {
  af: "Afrikaans",
  sq: "Albanian",
  am: "Amharic",
  ar: "Arabic",
  hy: "Armenian",
  az: "Azerbaijani",
  eu: "Basque",
  be: "Belarusian",
  bn: "Bengali",
  bs: "Bosnian",
  bg: "Bulgarian",
  ca: "Catalan",
  ceb: "Cebuano",
  ny: "Chichewa",
  "zh-CN": "Chinese (Simplified)",
  "zh-TW": "Chinese (Traditional)",
  co: "Corsican",
  hr: "Croatian",
  cs: "Czech",
  da: "Danish",
  nl: "Dutch",
  en: "English",
  eo: "Esperanto",
  et: "Estonian",
  tl: "Filipino",
  fi: "Finnish",
  fr: "French",
  fy: "Frisian",
  gl: "Galician",
  ka: "Georgian",
  de: "German",
  el: "Greek",
  gu: "Gujarati",
  ht: "Haitian Creole",
  ha: "Hausa",
  haw: "Hawaiian",
  iw: "Hebrew",
  hi: "Hindi",
  hmn: "Hmong",
  hu: "Hungarian",
  is: "Icelandic",
  ig: "Igbo",
  id: "Indonesian",
  ga: "Irish",
  it: "Italian",
  ja: "Japanese",
  jw: "Javanese",
  kn: "Kannada",
  kk: "Kazakh",
  km: "Khmer",
  ko: "Korean",
  ku: "Kurdish",
  ky: "Kyrgyz",
  lo: "Lao",
  la: "Latin",
  lv: "Latvian",
  lt: "Lithuanian",
  lb: "Luxembourgish",
  mk: "Macedonian",
  mg: "Malagasy",
  ms: "Malay",
  ml: "Malayalam",
  mt: "Maltese",
  mi: "Maori",
  mr: "Marathi",
  mn: "Mongolian",
  my: "Myanmar",
  ne: "Nepali",
  no: "Norwegian",
  ps: "Pashto",
  fa: "Persian",
  pl: "Polish",
  pt: "Portuguese",
  pa: "Punjabi",
  ro: "Romanian",
  ru: "Russian",
  sm: "Samoan",
  gd: "Scottish Gaelic",
  sr: "Serbian",
  st: "Sesotho",
  sn: "Shona",
  sd: "Sindhi",
  si: "Sinhala",
  sk: "Slovak",
  sl: "Slovenian",
  so: "Somali",
  es: "Spanish",
  su: "Sundanese",
  sw: "Swahili",
  sv: "Swedish",
  tg: "Tajik",
  ta: "Tamil",
  te: "Telugu",
  th: "Thai",
  tr: "Turkish",
  uk: "Ukrainian",
  ur: "Urdu",
  uz: "Uzbek",
  vi: "Vietnamese",
  cy: "Welsh",
  xh: "Xhosa",
  yi: "Yiddish",
  yo: "Yoruba",
  zu: "Zulu",
};

// Region groupings for languages (keys are region IDs, not display names)
const REGIONS = {
  yourLanguage: [], // Will be populated dynamically
  northAndSouthAmerica: ["en", "es", "pt", "fr"],
  europe: [
    "de",
    "it",
    "nl",
    "pl",
    "ru",
    "uk",
    "el",
    "sv",
    "no",
    "da",
    "fi",
    "cs",
    "hu",
    "ro",
    "sr",
    "hr",
    "sl",
    "sk",
    "bg",
    "sq",
    "is",
    "et",
    "lv",
    "lt",
    "ga",
  ],
  asiaPacific: [
    "zh-CN",
    "zh-TW",
    "ja",
    "ko",
    "th",
    "vi",
    "id",
    "ms",
    "tl",
    "hi",
    "bn",
    "ta",
    "te",
    "mr",
    "gu",
    "kn",
    "ml",
    "pa",
    "ur",
    "ne",
    "si",
    "my",
    "km",
    "lo",
    "mn",
  ],
  middleEast: ["ar", "iw", "fa", "tr"],
  africa: ["sw", "af", "am", "so", "ha", "yo"],
};

export default class extends Controller {
  static targets = ["modal", "loading", "content", "button", "spinner", "icon"];
  static values = {
    defaultLanguage: { type: String, default: "es" },
    languages: { type: Array, default: ["es", "fr", "it", "en"] },
    regionNames: { type: Object, default: {} },
  };

  connect() {
    this.googleTranslateLoaded = false;
    // Defer to ensure targets are available after Turbo morphdom
    requestAnimationFrame(() => {
      if (this.hasContentTarget) {
        this.populateLanguages();
      }
    });
  }

  get currentLanguage() {
    const cookieMatch = document.cookie.match(
      "(^|;) ?googtrans=([^;]*)(;|$)"
    );
    if (cookieMatch) {
      return cookieMatch[2].split("/")[2];
    }
    return this.defaultLanguageValue;
  }

  get browserLanguage() {
    const lang = (navigator.language || navigator.userLanguage || "").toLowerCase();
    // Handle special cases
    if (lang === "zh" || lang === "zh-cn") return "zh-CN";
    if (lang === "zh-tw" || lang === "zh-hk") return "zh-TW";
    if (lang === "he") return "iw";
    return lang.substr(0, 2);
  }

  populateLanguages() {
    if (!this.hasContentTarget) return;

    const container = this.contentTarget;
    container.innerHTML = "";

    const currentLang = this.currentLanguage;
    const browserLang = this.browserLanguage;
    const availableLanguages = this.languagesValue;
    const regionNames = this.regionNamesValue;

    // Build regions with available languages only
    const regionsToRender = {};

    // Add browser language to "Your language" if available
    if (
      availableLanguages.includes(browserLang) &&
      LANGUAGE_NAMES[browserLang]
    ) {
      regionsToRender.yourLanguage = [browserLang];
    }

    // Add other regions
    Object.entries(REGIONS).forEach(([regionId, langs]) => {
      if (regionId === "yourLanguage") return;

      const available = langs.filter((lang) =>
        availableLanguages.includes(lang)
      );
      if (available.length > 0) {
        regionsToRender[regionId] = available;
      }
    });

    // Render each region
    Object.entries(regionsToRender).forEach(([regionId, langs]) => {
      if (langs.length === 0) return;

      const regionName = regionNames[regionId] || regionId;
      this.renderRegion(container, regionName, langs, currentLang);
    });

    this.showContent();
  }

  renderRegion(container, regionName, languages, currentLang) {
    const regionHeader = document.createElement("div");
    regionHeader.className = "language-region-header";
    regionHeader.textContent = regionName;
    container.appendChild(regionHeader);

    const grid = document.createElement("div");
    grid.className = "language-grid";

    languages.forEach((langCode) => {
      const langName = LANGUAGE_NAMES[langCode];
      if (!langName) return;

      const countryCode = LANGUAGE_TO_COUNTRY[langCode] || "un";

      const item = document.createElement("button");
      item.className = "language-item";
      item.type = "button";
      if (langCode === currentLang) {
        item.classList.add("active");
      }
      item.dataset.langCode = langCode;
      item.dataset.action = "click->language-selector#selectLanguage";

      const flag = document.createElement("img");
      flag.src = `https://flagcdn.com/20x15/${countryCode}.png`;
      flag.srcset = `https://flagcdn.com/40x30/${countryCode}.png 2x`;
      flag.width = 20;
      flag.height = 15;
      flag.alt = langName;
      flag.className = "language-flag";

      const name = document.createElement("span");
      name.className = "language-name";
      name.textContent = langName;

      item.appendChild(flag);
      item.appendChild(name);
      grid.appendChild(item);
    });

    container.appendChild(grid);
  }

  openModal() {
    if (!this.hasModalTarget) return;

    this.modalTarget.showModal();

    if (this.hasContentTarget && this.contentTarget.children.length > 0) {
      this.showContent();
    }
  }

  closeModal() {
    if (!this.hasModalTarget) return;

    this.modalTarget.close();
  }

  showContent() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden");
    }
    if (this.hasContentTarget) {
      this.contentTarget.classList.remove("hidden");
    }
  }

  selectLanguage(event) {
    const langCode = event.currentTarget.dataset.langCode;

    // Update active state visually
    if (this.hasContentTarget) {
      this.contentTarget.querySelectorAll(".language-item").forEach((el) => {
        el.classList.remove("active");
      });
    }
    event.currentTarget.classList.add("active");

    // Show loading state
    this.setButtonLoading(true);

    // Trigger translation
    this.doTranslate(langCode);

    // Close modal after a brief delay
    setTimeout(() => this.closeModal(), 150);
  }

  doTranslate(targetLang) {
    // If selecting the default language and no translation is active, do nothing
    if (
      this.currentLanguage === this.defaultLanguageValue &&
      targetLang === this.defaultLanguageValue
    ) {
      this.setButtonLoading(false);
      return;
    }

    // Load Google Translate library if not loaded
    if (!this.googleTranslateLoaded) {
      this.loadGoogleTranslate(() => {
        this.executeTranslation(targetLang);
      });
    } else {
      this.executeTranslation(targetLang);
    }
  }

  loadGoogleTranslate(callback) {
    // Create the hidden element for Google Translate
    if (!document.getElementById("google_translate_element2")) {
      const el = document.createElement("div");
      el.id = "google_translate_element2";
      el.style.display = "none";
      document.body.appendChild(el);
    }

    // Define the callback
    window.googleTranslateElementInit2 = () => {
      new google.translate.TranslateElement(
        {
          pageLanguage: this.defaultLanguageValue,
          autoDisplay: false,
        },
        "google_translate_element2"
      );
      this.googleTranslateLoaded = true;
      if (callback) callback();
    };

    // Load the script
    if (!window.gt_translate_script) {
      window.gt_translate_script = document.createElement("script");
      window.gt_translate_script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit2";
      document.body.appendChild(window.gt_translate_script);
    }
  }

  executeTranslation(targetLang) {
    // Find the Google Translate combo box
    const selects = document.getElementsByTagName("select");
    let teCombo = null;
    for (let i = 0; i < selects.length; i++) {
      if (selects[i].className.indexOf("goog-te-combo") !== -1) {
        teCombo = selects[i];
        break;
      }
    }

    const translateElement = document.getElementById(
      "google_translate_element2"
    );
    if (
      !translateElement ||
      translateElement.innerHTML.length === 0 ||
      !teCombo ||
      teCombo.length === 0 ||
      teCombo.innerHTML.length === 0
    ) {
      // Not ready yet, retry
      setTimeout(() => this.executeTranslation(targetLang), 500);
      return;
    }

    // Set the value and trigger change
    teCombo.value = targetLang;
    this.fireEvent(teCombo, "change");
    this.fireEvent(teCombo, "change");

    // Update UI after translation completes
    setTimeout(() => {
      this.setButtonLoading(false);
      this.populateLanguages(); // Refresh to show new active state
    }, 1000);
  }

  fireEvent(element, eventName) {
    try {
      if (document.createEventObject) {
        const evt = document.createEventObject();
        element.fireEvent("on" + eventName, evt);
      } else {
        const evt = document.createEvent("HTMLEvents");
        evt.initEvent(eventName, true, true);
        element.dispatchEvent(evt);
      }
    } catch (e) {
      console.error("Error firing event:", e);
    }
  }

  setButtonLoading(loading) {
    this.spinnerTargets.forEach((spinner) => {
      spinner.classList.toggle("hidden", !loading);
    });
    this.iconTargets.forEach((icon) => {
      icon.classList.toggle("hidden", loading);
    });
    this.buttonTargets.forEach((button) => {
      button.disabled = loading;
    });
  }
}
